import { apiClient } from "@/services/apiClient";
import type { Page } from "@/types/api.types";
import type {
  NotificationResponse,
  NotificationFilters,
  UnreadCountResponse,
  RelevantEventsResponse,
  NotificationSettingsResponse,
  NotificationSettingsRequest,
  ManualSendRequest,
} from "../types";

/**
 * Notification API service.
 *
 * All calls go through `apiClient`, which prepends the base URL, attaches the
 * Bearer token, unwraps the `{ data }` envelope, returns `undefined` for 204,
 * and throws `ApiClientError` on non-2xx. See docs/notifications/notificationsapi.md.
 */
export const notificationService = {
  // ============================================
  // Inbox (read)
  // ============================================

  /** List my inbox (paged). `unreadOnly` uses a fast partial index. */
  getNotifications(filters?: NotificationFilters): Promise<Page<NotificationResponse>> {
    return apiClient.get<Page<NotificationResponse>>("/notifications", filters);
  },

  /** Badge number. Safe to poll frequently (O(log n)). */
  getUnreadCount(): Promise<UnreadCountResponse> {
    return apiClient.get<UnreadCountResponse>("/notifications/unread-count");
  },

  /** Fetch one (ownership-checked: 403 if not owner, 404 if missing/soft-deleted). */
  getNotification(id: string): Promise<NotificationResponse> {
    return apiClient.get<NotificationResponse>(`/notifications/${id}`);
  },

  // ============================================
  // Inbox (write)
  // ============================================

  /** Mark one read (idempotent). */
  markAsRead(id: string): Promise<void> {
    return apiClient.patch<undefined>(`/notifications/${id}/read`);
  },

  /** Mark all read; resolves to the count of rows just stamped. */
  markAllRead(): Promise<number> {
    return apiClient.post<number>("/notifications/mark-all-read");
  },

  /** Soft-delete (hides the row; no undo). Backend returns 204. */
  deleteNotification(id: string): Promise<void> {
    return apiClient.delete<undefined>(`/notifications/${id}`);
  },

  // ============================================
  // Settings
  // ============================================

  /** Get my settings (auto-creates defaults on first read — never 404s). */
  getSettings(): Promise<NotificationSettingsResponse> {
    return apiClient.get<NotificationSettingsResponse>("/notifications/settings");
  },

  /** Event types relevant to the current user's role(s) — for filtering the settings UI. */
  getRelevantEvents(): Promise<RelevantEventsResponse> {
    return apiClient.get<RelevantEventsResponse>("/notifications/settings/relevant-events");
  },

  /** Update my settings (PATCH-like: send only changed fields). Returns full state. */
  updateSettings(data: NotificationSettingsRequest): Promise<NotificationSettingsResponse> {
    return apiClient.put<NotificationSettingsResponse>("/notifications/settings", data);
  },

  // ============================================
  // HR-only
  // ============================================

  /** Manual send (HR only, audited). `data` is null if recipient opted out of both channels. */
  sendManual(data: ManualSendRequest): Promise<NotificationResponse | null> {
    return apiClient.post<NotificationResponse | null>("/notifications", data);
  },
};
